import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CreditCard, Download, Loader2, Printer, Receipt, Ticket } from "lucide-react";
import { toast } from "sonner";

import { billingApi } from "@/api";
import { guestRewardsApi } from "@/api/guest/guestRewardsApi.js";
import GuestLayout from "@/components/guest/GuestLayout.jsx";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BillLineItemsTable from "@/components/hotel/billing/BillLineItemsTable";
import BillPaymentsTable from "@/components/hotel/billing/BillPaymentsTable";
import BillTotalsCard from "@/components/hotel/billing/BillTotalsCard";
import { useSession } from "@/hooks/useSession.js";
import {
  formatBillStatus,
  formatDateTime,
  getApiData,
  getApiMessage,
  getBillStatusBadgeClass,
  normalizeBill,
} from "@/utils/billingFormat";
import { downloadBlobResponse } from "@/utils/fileDownload";

const GuestBillingPage = () => {
  const navigate = useNavigate();
  const { session, hotel, room, loading: sessionLoading } = useSession();
  const [bill, setBill] = useState(null);
  const [paymentOptions, setPaymentOptions] = useState(null);
  const [couponData, setCouponData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    if (!sessionLoading && !session) {
      navigate("/guest/expired", { replace: true });
    }
  }, [sessionLoading, session, navigate]);

  useEffect(() => {
    const loadBill = async () => {
      if (!session) return;

      try {
        setLoading(true);
        const [response, optionsResponse, couponsResponse] = await Promise.all([
          billingApi.getMyBill(),
          billingApi.getGuestPaymentOptions().catch(() => null),
          guestRewardsApi.getCoupons().catch(() => null),
        ]);
        const data = getApiData(response);
        setBill(data ? normalizeBill(data) : null);
        setPaymentOptions(getApiData(optionsResponse));
        const coupons = getApiData(couponsResponse);
        setCouponData(coupons);
      } catch (error) {
        if (error?.response?.status !== 404) {
          toast.error(getApiMessage(error, "Unable to load bill."));
        }
        setBill(null);
      } finally {
        setLoading(false);
      }
    };

    loadBill();
  }, [session]);

  if (sessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) return null;

  const title = bill?.status === "Draft" ? "Estimated Bill" : "My Bill";
  const availableCoupons = getAvailableCoupons(couponData);
  const handleDownloadPdf = async () => {
    try {
      setPdfLoading("download");
      const response = await billingApi.downloadMyInvoice();
      downloadBlobResponse(response, `GuestBill-${bill?.billNumber || "MyBill"}.pdf`);
      toast.success("Bill PDF downloaded successfully.");
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to download bill PDF."));
    } finally {
      setPdfLoading("");
    }
  };

  const handlePrintPdf = async () => {
    window.print();
  };

  const handlePayOnline = async () => {
    try {
      setPaymentLoading(true);
      const response = await billingApi.initiateGuestOnlinePayment();
      const data = getApiData(response);
      toast.success(data?.message || "Payment request created successfully.");
      if (data?.paymentUrl) {
        window.location.href = data.paymentUrl;
      }
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to start online payment."));
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${title} - Guest Portal`}</title>
        <meta name="description" content="View your hotel bill" />
      </Helmet>

      <GuestLayout hotel={hotel} room={room} session={session}>
        <div className="mx-auto max-w-5xl space-y-5">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/guest/home")}
            className="bg-white/85 text-slate-800 hover:bg-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <div className="rounded-3xl border border-white/75 bg-white/90 p-5 shadow-[0_22px_52px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Receipt className="h-6 w-6 text-[#923536]" />
                  <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  Room {room?.roomNumber || room?.name || room?.Name || "—"}
                </p>
              </div>
              {bill && (
                <Badge className={getBillStatusBadgeClass(bill.status)}>
                  {bill.status === "Draft" ? "Estimated Bill" : formatBillStatus(bill.status)}
                </Badge>
              )}
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-slate-600">
                <Loader2 className="h-10 w-10 animate-spin text-[#923536]" />
                <span>Loading bill...</span>
              </div>
            ) : !bill ? (
              <div className="mt-6 rounded-2xl border border-dashed p-8 text-center text-sm text-slate-600">
                Your bill is not available yet. Please contact reception.
              </div>
            ) : (
              <div className="mt-6 space-y-6">
                <div className="rounded-2xl border bg-white p-4">
                  {bill.status === "Draft" && (
                    <p className="text-sm text-amber-800">
                      This is an estimated bill. Final charges may be updated by reception.
                    </p>
                  )}
                  {bill.status === "Paid" && (
                    <p className="text-sm text-emerald-700">
                      Your bill has been paid. Thank you.
                    </p>
                  )}
                  {bill.dueAmount > 0 && bill.status !== "Draft" && (
                    <p className="text-sm text-[#923536]">
                      {paymentOptions?.onlinePaymentEnabled
                        ? "You can pay online or contact reception to complete payment."
                        : "Please contact reception to complete payment."}
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDownloadPdf}
                      disabled={Boolean(pdfLoading)}
                    >
                      {pdfLoading === "download" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}
                      Download PDF
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePrintPdf}
                      disabled={Boolean(pdfLoading)}
                    >
                      <Printer className="mr-2 h-4 w-4" />
                      Print bill
                    </Button>
                    {bill.dueAmount > 0 && paymentOptions?.onlinePaymentEnabled && (
                      <Button
                        type="button"
                        onClick={handlePayOnline}
                        disabled={paymentLoading}
                        className="bg-[#923536] text-white hover:bg-[#7f2d2e]"
                      >
                        {paymentLoading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <CreditCard className="mr-2 h-4 w-4" />
                        )}
                        Pay online
                      </Button>
                    )}
                  </div>
                  {availableCoupons.length > 0 && (
                    <div className="mt-4 rounded-2xl border bg-slate-50 p-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Package event coupons</p>
                          <p className="mt-1 text-xs text-slate-600">
                            Show these QR coupons to staff when you use an included package event.
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => navigate("/guest/coupons")}
                          className="border-[#923536]/25 text-[#923536] hover:bg-[#923536]/10"
                        >
                          <Ticket className="mr-2 h-4 w-4" />
                          View coupons
                        </Button>
                      </div>
                    </div>
                  )}
                  {bill.dueAmount > 0 && !paymentOptions?.onlinePaymentEnabled && (
                    <p className="mt-3 text-xs text-slate-500">
                      {paymentOptions?.message || "Online payment is not configured for this hotel."}
                    </p>
                  )}
                </div>

                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                  <div className="rounded-2xl border bg-white p-4">
                    <p className="mb-4 font-semibold text-slate-900">Stay details</p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <p className="text-xs text-slate-500">Bill number</p>
                        <p className="mt-1 font-semibold text-slate-900">{bill.billNumber}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Guest name</p>
                        <p className="mt-1 font-semibold text-slate-900">{bill.guestName}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Check-in</p>
                        <p className="mt-1 font-semibold text-slate-900">
                          {formatDateTime(bill.checkInAtUtc)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Check-out</p>
                        <p className="mt-1 font-semibold text-slate-900">
                          {formatDateTime(bill.checkOutAtUtc)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <BillTotalsCard bill={bill} />
                </div>

                <section className="space-y-3">
                  <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                    <Receipt className="h-5 w-5 text-[#923536]" />
                    Charges
                  </h2>
                  <BillLineItemsTable items={bill.lineItems} currencyCode={bill.currencyCode} />
                </section>

                <section className="space-y-3">
                  <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                    <CreditCard className="h-5 w-5 text-[#923536]" />
                    Payments
                  </h2>
                  <BillPaymentsTable payments={bill.payments} currencyCode={bill.currencyCode} />
                </section>
              </div>
            )}
          </div>
        </div>
      </GuestLayout>
    </>
  );
};

const getAvailableCoupons = (couponData) => {
  if (couponData?.requiresRegistration || couponData?.RequiresRegistration) return [];
  return (couponData?.eventCoupons || couponData?.EventCoupons || []).filter((coupon) => coupon?.couponCode || coupon?.CouponCode);
};

export default GuestBillingPage;
